import React, { useEffect, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import axios, { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import { useAuth } from '../../../hooks/useAuth';
import { Actions, Categories } from '../../../types/gtm';
import Icon from '../../../components/Section/Icon';
import googleIcon from '../../../static/img/google-icon.svg';
import { getStatusColor } from '../../../util/StyleAvatar';
import {
  Content,
  NameAccount,
  StyleSubTitle,
  StyleTitle,
  SubTitle,
} from '../styles';
import StatusAccount from '../components/StatusAccount';
import Toogle from '../../../components/Section/Toogle';
import { api } from '../../../services/api';
import { Accounts } from '../../../types/Accounts';
import environmentConfig from '../../../config/environment-configs';
import Title from '../../../components/Title';

export type ManageAccounts = {
  contasInfo: Accounts;
};

interface GoogleTokenResponse {
  /* eslint-disable camelcase */
  access_token: string;
}
export interface GoogleUserInfo {
  sub: number;
  picture: string;
  email: string;
}

const BoxProviders = ({ contasInfo }: ManageAccounts) => {
  const { authenticateOnAPI, setError, setSuccess, user } = useAuth();
  const [connectionOn] = useState<string>('Conectado');
  const [connectionOff] = useState<string>('Não conectado');
  const [googleAssociated, setGoogleAssociated] = useState<boolean>(false);
  const clientGoogle = useRef<any>(null);

  const associateGoogleAccount = async (
    googleUserId: string,
    photoUrl: string,
    emailOnProvider: string,
  ) => {
    const body = {
      external_id: googleUserId,
      photo_url: photoUrl,
      provider: 'google',
      email_on_provider: emailOnProvider,
    };
    const token = await authenticateOnAPI();
    api
      .post('/accounts/google-account', body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then((response: AxiosResponse) => {
        if (response.status === 200) {
          setSuccess('Sua conta Google foi associada.');
          setGoogleAssociated(true);
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: Categories.AssociateAccounts,
            event_action: Actions.ClickGoogleAssociate,
            bd_suser_code: user?.globoId,
          });
        }
      })
      .catch((error: AxiosError) => {
        window.dataLayer.push({
          event: 'customEventUserInteraction',
          event_category: Categories.AssociateAccounts,
          event_action: Actions.Error,
          event_label: Actions.ErrorAddGoogle,
          bd_suser_code: user?.globoId,
        });
        setGoogleAssociated(false);
        if (error.response?.status === 409) {
          setError(
            'Não foi possível associar sua conta Google, pois ela já encontra-se associada a outra conta de usuário.',
          );
        } else {
          setError(
            'Não foi possível associar sua conta Google, aguarde alguns instantes e tente novamente.',
          );
        }
      });
  };
  const connectGoogleAccount = async (response: GoogleTokenResponse) => {
    const msgError =
      'Não foi possível associar sua conta Google, aguarde alguns instantes e tente novamente.';

    if (response && response.access_token) {
      axios
        .get<GoogleUserInfo>('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${response.access_token}` },
        })
        .then(({ data }) => {
          associateGoogleAccount(data.sub.toString(), data.picture, data.email);
        })
        .catch(() => {
          toast.error(msgError);
        });
    } else {
      toast.error(msgError);
    }
  };
  function setAssociations() {
    if (contasInfo.google.userId) {
      setGoogleAssociated(true);
    }
  }
  useEffect(() => {
    setAssociations();
    if (typeof (window as any).google !== 'undefined') {
      const client = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: environmentConfig.GOOGLE_CLIENT_ID,
        scope: `openid profile email`,
        callback: connectGoogleAccount,
        error_callback: () => {
          toast.error(
            'Não foi possível associar sua conta Google, aguarde alguns instantes e tente novamente.',
          );
        },
      });
      clientGoogle.current = client;
    }
  }, []);
  const disassociateGoogleAccount = async () => {
    const token = await authenticateOnAPI();
    const data = {
      provider: 'google',
    };
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    api
      .delete('/accounts/google-account', { headers, data })
      .then((response: AxiosResponse) => {
        if (response.status === 200) {
          setSuccess('Sua conta Google foi desassociada.');
          setGoogleAssociated(!googleAssociated);
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: Categories.AssociateAccounts,
            event_action: Actions.ClickGoogleDisassociate,
            bd_suser_code: user?.globoId,
          });
        }
      })
      .catch(() => {
        setGoogleAssociated(googleAssociated);
        toast.error(
          'Não foi possível desassociar sua conta Google, aguarde alguns instantes e tente novamente.',
        );
      });
  };
  const HandleGoogle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const label = moment().format('DD/MM/YYYY');

    if (target.checked) {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.AssociateAccounts,
        event_action: Actions.ClickAddGoogleAccount,
        event_label: label,
        bd_suser_code: user?.globoId,
      });
      clientGoogle.current?.requestAccessToken();
    } else {
      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.AssociateAccounts,
        event_action: Actions.ClickRemoveGoogleAccount,
        event_label: label,
        bd_suser_code: user?.globoId,
      });
      disassociateGoogleAccount();
    }
  };
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="colored"
        pauseOnHover={false}
      />
      <StyleTitle>
        <Title variant="page">Contas associadas</Title>
        <p>
          Aqui você pode conectar e desconectar suas contas de apps e sites
          associados à sua Conta Globo
        </p>
      </StyleTitle>
      <div role="group" aria-label="Lista 1 item">
        <StyleSubTitle>
          <SubTitle>Conectou à Conta Globo</SubTitle>
          <p>
            Estes são os apps e sites que você utilizou ou pode utilizar para
            conectar-se à Conta Globo
          </p>
        </StyleSubTitle>
        <Content>
          <div>
            <div>
              <Icon svg={googleIcon} alt="google-icon" />
              <div>
                <NameAccount
                  aria-label={`Google ${
                    googleAssociated ? connectionOn : connectionOff
                  }`}
                >
                  Google
                </NameAccount>
                <StatusAccount
                  statusColor={getStatusColor(
                    googleAssociated ? connectionOn : connectionOff,
                  )}
                  status={googleAssociated ? connectionOn : connectionOff}
                />
              </div>
            </div>
            <Toogle
              checked={googleAssociated}
              onChange={HandleGoogle}
              htmlFor="google-toogle"
              aria-label={
                googleAssociated ? `Desativar conexão` : `Adicionar conexão`
              }
            />
          </div>
        </Content>
      </div>
    </>
  );
};
export default BoxProviders;
